import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SubpageNav from '../../components/SubpageNav';

export default function InternationalStandardsPage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SubpageNav backToHomeKey="internationalStandards.backToHome" logoSubtitle={t('internationalStandards.companyName')} />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-yellow-50 to-yellow-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-3 bg-yellow-200 rounded-full mb-6">
              <span className="text-yellow-900 font-bold text-sm whitespace-nowrap">{t('internationalStandards.badge')}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6">
              {t('internationalStandards.title')}
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {t('internationalStandards.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: 'ri-award-line', 
                title: 'ISO', 
                count: '17025',
                color: 'from-blue-500 to-blue-600'
              },
              { 
                icon: 'ri-flashlight-line', 
                title: 'IEC', 
                count: '60068',
                color: 'from-purple-500 to-purple-600'
              },
              { 
                icon: 'ri-global-line', 
                title: 'ILAC MRA', 
                count: '100+',
                color: 'from-teal-500 to-teal-600'
              },
              { 
                icon: 'ri-flask-line', 
                title: 'OECD GLP', 
                count: 'Certified',
                color: 'from-orange-500 to-orange-600'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all">
                <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-3xl font-bold text-teal-600">{item.count}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ISO Standards Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 rounded-full mb-6">
                <span className="text-blue-700 font-bold text-sm whitespace-nowrap">ISO Standards</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
                {t('internationalStandards.isoTitle')}
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {t('internationalStandards.isoDesc')}
              </p>
              <div className="space-y-4">
                {(t('internationalStandards.isoPoints', { returnObjects: true }) as string[]).map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-blue-600 text-sm"></i>
                    </div>
                    <p className="text-gray-700 font-medium">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-96 w-full rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/4d9e8aa14ee505b000df01800c32e927.jpg"
                alt="ISO 17025 Certificate"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-10">
            <h3 className="text-3xl font-extrabold text-gray-900 mb-8 text-center">{t('internationalStandards.isoRequirements.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: t('internationalStandards.isoRequirements.management.title'),
                  items: t('internationalStandards.isoRequirements.management.items', { returnObjects: true }) as string[]
                },
                {
                  title: t('internationalStandards.isoRequirements.technical.title'),
                  items: t('internationalStandards.isoRequirements.technical.items', { returnObjects: true }) as string[]
                },
                {
                  title: t('internationalStandards.isoRequirements.quality.title'),
                  items: t('internationalStandards.isoRequirements.quality.items', { returnObjects: true }) as string[]
                }
              ].map((category, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">{category.title}</h4>
                  <ul className="space-y-2">
                    {category.items.map((item, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <i className="ri-checkbox-circle-fill text-blue-600 mr-2"></i>
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IEC Standards Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 w-full rounded-2xl overflow-hidden shadow-2xl order-2 lg:order-1">
              <img 
                src="https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/17e9f6e5fe8aa9e8b5e2954fe680085b.jpeg"
                alt="IEC Testing"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-block px-4 py-2 bg-purple-100 rounded-full mb-6">
                <span className="text-purple-700 font-bold text-sm whitespace-nowrap">IEC Standards</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
                {t('internationalStandards.iecTitle')}
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {t('internationalStandards.iecDesc')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(t('internationalStandards.iecStandards', { returnObjects: true }) as Array<{code: string, name: string}>).map((standard, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-md border-l-4 border-purple-500">
                    <p className="text-sm font-bold text-purple-600 mb-1">{standard.code}</p>
                    <p className="text-gray-900 font-semibold">{standard.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ILAC MRA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-6">
              <span className="text-teal-700 font-bold text-sm whitespace-nowrap">ILAC MRA</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              {t('internationalStandards.ilacTitle')}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {t('internationalStandards.ilacDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {(t('internationalStandards.ilacBenefits', { returnObjects: true }) as Array<{title: string, description: string}>).map((benefit, index) => (
              <div key={index} className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${['ri-global-line', 'ri-shield-check-line', 'ri-file-text-line'][index]} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-700 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-12 text-white text-center shadow-xl">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-earth-line text-5xl text-white"></i>
            </div>
            <h3 className="text-3xl font-extrabold mb-4">{t('internationalStandards.ilacCountries')}</h3>
            <p className="text-white/90 text-lg mb-8 max-w-3xl mx-auto">
              {t('internationalStandards.ilacCountriesDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {(t('internationalStandards.countries', { returnObjects: true }) as string[]).map((country, index) => (
                <span key={index} className="px-4 py-2 bg-white/20 rounded-full text-sm font-semibold whitespace-nowrap">
                  {country}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OECD GLP Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-orange-100 rounded-full mb-6">
                <span className="text-orange-700 font-bold text-sm whitespace-nowrap">OECD GLP</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
                {t('internationalStandards.oecdTitle')}
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {t('internationalStandards.oecdDesc')}
              </p>
              <div className="space-y-6">
                {(t('internationalStandards.oecdItems', { returnObjects: true }) as Array<{title: string, description: string}>).map((item, index) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-md border-l-4 border-orange-500">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-96 w-full rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20chemistry%20laboratory%20with%20scientists%20conducting%20safety%20tests%2C%20OECD%20GLP%20compliant%20testing%20facility%2C%20clean%20white%20laboratory%20environment%2C%20chemical%20analysis%20equipment%2C%20quality%20assurance%20procedures%2C%20organized%20workspace%20with%20documentation&width=800&height=600&seq=oecd-glp-003&orientation=landscape"
                alt="OECD GLP Laboratory"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-br from-yellow-50 to-yellow-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              {t('internationalStandards.benefitsTitle')}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {t('internationalStandards.benefitsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(t('internationalStandards.benefits', { returnObjects: true }) as Array<{title: string, description: string}>).map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${['ri-checkbox-circle-line', 'ri-shield-check-line', 'ri-money-dollar-circle-line', 'ri-global-line', 'ri-file-shield-line', 'ri-customer-service-2-line'][index]} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
            {t('internationalStandards.ctaTitle')}
          </h2>
          <p className="text-xl text-gray-700 mb-10 leading-relaxed">
            {t('internationalStandards.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/?section=contact"
              className="px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              {t('internationalStandards.ctaButton1')}
            </Link>
            <Link 
              to="/"
              className="px-8 py-4 bg-gray-100 text-gray-900 rounded-lg font-semibold text-lg hover:bg-gray-200 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line mr-2"></i>
              {t('internationalStandards.ctaButton2')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/61ea8732-385a-4541-8aab-5452a62776b3.png" 
                  alt="Logo" 
                  className="h-10 w-auto"
                />
                <div>
                  <h3 className="text-lg font-bold">Testing Laboratory</h3>
                  <p className="text-xs text-gray-400">ISO 17025 Certified</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                ILAC 인정 국제 공인 시험소로서 최고 수준의 시험 분석 서비스를 제공합니다
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">국제 규격</h4>
              <ul className="space-y-2">
                <li className="text-gray-400 text-sm">ISO/IEC 17025</li>
                <li className="text-gray-400 text-sm">IEC Standards</li>
                <li className="text-gray-400 text-sm">ILAC MRA</li>
                <li className="text-gray-400 text-sm">OECD GLP</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">바로가기</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">홈</Link></li>
                <li><Link to="/?section=services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">서비스</Link></li>
                <li><Link to="/?section=certifications" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">인증</Link></li>
                <li><Link to="/?section=contact" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">문의</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">연락처</h4>
              <ul className="space-y-3">
                <li className="flex items-center space-x-2 text-sm text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>+82-2-1234-5678</span>
                </li>
                <li className="flex items-center space-x-2 text-sm text-gray-400">
                  <i className="ri-mail-line"></i>
                  <span>info@testinglab.com</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                © 2024 ISO 17025 Testing Laboratory. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
